/**
 * @voltxampere/engine — ERC rule: overvoltage (Sprint 56)
 *
 * Node voltage exceeds an explicit rating declared in the component
 * params. Catches the "DC OP at 240 V on a node feeding a 5 V part"
 * authoring bug before the simulator silently produces a numerically-
 * correct-but-physically-wrong waveform.
 *
 * Detection heuristic (S56a MVP):
 *   - Component declares `voltageRating` in params (V) — opt-in;
 *     library variants may carry it (e.g. 5 V zener, 16 V capacitor).
 *   - Reads attached net voltages from DC OP. If |V| at any terminal
 *     exceeds rating × safety margin (default 0.9), flag a warning.
 *
 * Sprint 57+ may add per-pin rating (BJT BVCEO vs BVCBO) and a
 * per-component-type fallback when params don't carry a rating
 * (datasheet-derived from variant registry).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const SAFETY_MARGIN = 0.9; // flag when |V| > rating × 0.9
export const overvoltageRule = {
    id: "overvoltage",
    defaultSeverity: "warn",
    requiresDCResult: true,
    check(circuit, dcResult) {
        if (dcResult === undefined)
            return [];
        const violations = [];
        for (const c of circuit.components) {
            const params = c.params;
            const ratingRaw = params.voltageRating;
            if (typeof ratingRaw !== "number" || !Number.isFinite(ratingRaw) || ratingRaw <= 0)
                continue;
            const threshold = ratingRaw * SAFETY_MARGIN;
            let worstAbsV = 0;
            let worstNetId = "";
            for (const t of c.terminals) {
                const v = dcResult.nodeVoltages.get(t.netId);
                if (typeof v !== "number")
                    continue;
                if (Math.abs(v) > worstAbsV) {
                    worstAbsV = Math.abs(v);
                    worstNetId = t.netId;
                }
            }
            if (worstAbsV > threshold) {
                violations.push({
                    ruleId: "overvoltage",
                    severity: "warn",
                    i18nKey: "erc.overvoltage.message",
                    suggestionKey: "erc.overvoltage.suggestion",
                    meta: {
                        componentId: c.id,
                        componentType: c.type,
                        voltageRating: ratingRaw,
                        observedVoltage: worstAbsV,
                        worstNetId,
                        safetyMargin: SAFETY_MARGIN,
                    },
                });
            }
        }
        return violations;
    },
};
//# sourceMappingURL=overvoltage.js.map